import React, { useState, useEffect, useContext } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
  PieChart, Pie, LineChart, Line,
} from "recharts";
import Header from "../components/HeaderB";
import ProfileSidebar from "../components/ProfileSideBar";
import { AuthContext } from "../context/AuthContext";
import { Users, Tag, Store, Star, Heart, TrendingUp, BarChart2, ShoppingCart, Info, X } from "lucide-react";

// Theme gradient palette
const COLORS = ["#D85FEC", "#B86AF2", "#A870F5", "#817EFD", "#C065EE", "#9B7EF0", "#D070F8", "#7580FF"];

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topStoreCity, setTopStoreCity] = useState("");
  const [openTip, setOpenTip] = useState(null);
  const [isHighContrast, setIsHighContrast] = useState(
    document.documentElement.classList.contains('accessibility-mode')
  );
  const { user, accessToken, language } = useContext(AuthContext);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsHighContrast(document.documentElement.classList.contains('accessibility-mode'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const translations = {
    el: {
      title: "Στατιστικά",
      totalUsers: "Χρήστες",
      totalDiscounts: "Προσφορές",
      activeDiscounts: "Ενεργές",
      expiredDiscounts: "Ληγμένες",
      totalStores: "Καταστήματα",
      totalCategories: "Κατηγορίες",
      totalRatings: "Βαθμολογίες",
      totalFavorites: "Αγαπημένα",
      tipUsers: "Συνολικός αριθμός εγγεγραμμένων χρηστών στην πλατφόρμα.",
      tipDiscounts: "Συνολικός αριθμός προσφορών που έχουν δημοσιευτεί.",
      tipActive: "Αριθμός προσφορών που είναι αυτή τη στιγμή ενεργές.",
      tipExpired: "Αριθμός προσφορών που έχουν λήξει.",
      tipStores: "Συνολικός αριθμός καταστημάτων που είναι καταχωρημένα.",
      tipCategories: "Αριθμός κατηγοριών προϊόντων που χρησιμοποιούνται.",
      tipRatings: "Συνολικός αριθμός αξιολογήσεων που έχουν δοθεί από χρήστες.",
      tipFavorites: "Συνολικός αριθμός φορών που μια προσφορά έχει αποθηκευτεί ως αγαπημένο.",
      discountStatus: "Κατάσταση προσφορών",
      discountsByCategory: "Προσφορές ανά κατηγορία (κορυφαίες 12)",
      discountsPerMonth: "Νέες προσφορές ανά μήνα (τελευταίοι 12 μήνες)",
      usersPerMonth: "Νέοι χρήστες ανά μήνα (τελευταίοι 12 μήνες)",
      topStores: "Κορυφαία καταστήματα (βάσει αριθμού προσφορών)",
      allCities: "Όλες οι πόλεις",
      discountsByCity: "Προσφορές ανά πόλη",
      heatLow: "Λίγες",
      heatHigh: "Πολλές",
      active: "Ενεργές",
      expired: "Ληγμένες",
      count: "Αριθμός",
      loading: "Φόρτωση...",
    },
    en: {
      title: "Analytics",
      totalUsers: "Users",
      totalDiscounts: "Offers",
      activeDiscounts: "Active",
      expiredDiscounts: "Expired",
      totalStores: "Stores",
      totalCategories: "Categories",
      totalRatings: "Ratings",
      totalFavorites: "Favorites",
      tipUsers: "Total number of registered users on the platform.",
      tipDiscounts: "Total number of offers that have been published.",
      tipActive: "Number of offers that are currently active.",
      tipExpired: "Number of offers that have expired.",
      tipStores: "Total number of stores registered in the system.",
      tipCategories: "Number of product categories in use.",
      tipRatings: "Total number of ratings submitted by users.",
      tipFavorites: "Total number of times an offer has been saved as a favourite.",
      discountStatus: "Offer status",
      discountsByCategory: "Offers by category (top 12)",
      discountsPerMonth: "New offers per month (last 12 months)",
      usersPerMonth: "New users per month (last 12 months)",
      topStores: "Top stores by number of offers",
      allCities: "All cities",
      discountsByCity: "Offers by city",
      heatLow: "Few",
      heatHigh: "Many",
      active: "Active",
      expired: "Expired",
      count: "Count",
      loading: "Loading...",
    },
  };

  const t = translations[language] || translations.el;

  useEffect(() => {
    const fetchStats = async () => {
      if (!user || !accessToken) return;
      try {
        setLoading(true);
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/adminstats/stats`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const data = await res.json();
        setStats(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [user, accessToken]);

  if (loading) return (
    <div id="admindashboard-loading" className="flex justify-center items-center h-screen w-screen bg-custom-background">
      {t.loading}
    </div>
  );

  if (!stats) return null;

  const { kpis, statusData, discountsByCat, discountsPerMonth, usersPerMonth, topStores, discountsByCity } = stats;

  const kpiCards = [
    { label: t.totalUsers,      value: kpis.totalUsers,      icon: <Users     size={20} />, tip: t.tipUsers },
    { label: t.totalDiscounts,  value: kpis.totalDiscounts,  icon: <ShoppingCart size={20} />, tip: t.tipDiscounts },
    { label: t.activeDiscounts, value: kpis.activeDiscounts, icon: <TrendingUp size={20} />, tip: t.tipActive },
    { label: t.expiredDiscounts,value: kpis.expiredDiscounts,icon: <X  size={20} />, tip: t.tipExpired },
    { label: t.totalStores,     value: kpis.totalStores,     icon: <Store     size={20} />, tip: t.tipStores },
    { label: t.totalCategories, value: kpis.totalCategories, icon: <Tag       size={20} />, tip: t.tipCategories },
    { label: t.totalRatings,    value: kpis.totalRatings,    icon: <Star      size={20} />, tip: t.tipRatings },
    { label: t.totalFavorites,  value: kpis.totalFavorites,  icon: <Heart     size={20} />, tip: t.tipFavorites },
  ];

  // Localise names for each dataset
  const pieData = statusData.map(d => ({
    name:  language === 'el' ? d.name_el : d.name_en,
    value: d.value,
  }));

  const catData = discountsByCat.map(d => ({
    name:  language === 'el' ? d.name_el : d.name_en,
    count: d.count,
  }));

  const storeData = topStores.map(d => ({
    name:  language === 'el' ? d.name_el : d.name_en,
    city:  language === 'el' ? d.city_el : d.city_en,
    area:  language === 'el' ? d.area_el : d.area_en,
    count: d.count,
  }));

  const cityHeatData = (discountsByCity || []).map(d => ({
    name: language === 'el' ? d.city_el : d.city_en,
    size: d.count,
  }));

  const topStoreCities = [...new Set(
    (discountsByCity || []).map(d => language === 'el' ? d.city_el : d.city_en).filter(Boolean)
  )].sort();

  const filteredStoreData = (topStoreCity
    ? storeData.filter(d => d.city === topStoreCity)
    : storeData
  ).slice(0, 15);

  const ChartCard = ({ title, children, fullWidth = false, action = null }) => (
    <div className={`bg-custom-header rounded-2xl shadow p-5 ${fullWidth ? "col-span-1 md:col-span-2" : ""}`}>
      <div className="flex items-center justify-between mb-4 gap-2">
        <h3 className="text-sm font-semibold text-custom-letters">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen bg-custom-background flex flex-col">
      <Header />
      <div className="flex-1 flex flex-row relative bg-custom-background">
        <ProfileSidebar />
        <main id="admindashboard-content" className="flex flex-col flex-1 items-center justify-start px-4 md:px-10 py-10 xl:ml-20" onClick={() => setOpenTip(null)}>

          {/* Page title */}
          <div className="w-full max-w-6xl mt-12 md:mt-0 mb-8">
            <h2 className="text-2xl font-semibold text-custom-letters flex items-center gap-2">
              <BarChart2 className="text-custom-grad1" /> {t.title}
            </h2>
          </div>

          {/* ── KPI Cards ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full max-w-6xl mb-8">
            {kpiCards.map((card, i) => (
              <div key={i}
                className="bg-custom-header rounded-2xl shadow p-5 flex flex-col items-center gap-2 text-center relative">
                <div
                  className="absolute top-3 right-3 text-custom-letters opacity-40 hover:opacity-80 cursor-pointer transition-opacity"
                  onClick={(e) => { e.stopPropagation(); setOpenTip(openTip === i ? null : i); }}
                >
                  <Info size={14} />
                  {openTip === i && (
                    <div className="absolute right-0 top-5 z-50 bg-custom-header border border-gray-200 rounded-lg shadow-lg p-2 text-xs text-custom-letters w-44 text-left font-normal leading-relaxed">
                      {card.tip}
                    </div>
                  )}
                </div>
                <div className="bg-gradient-to-br from-custom-grad1 to-custom-grad4 text-white rounded-full p-2">
                  {card.icon}
                </div>
                <span className="text-3xl font-bold text-custom-letters">{card.value}</span>
                <span className="text-xs text-custom-letters opacity-70">{card.label}</span>
              </div>
            ))}
          </div>

          {/* ── Charts grid ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-6xl mb-10">

            {/* Donut: Active vs Expired */}
            <ChartCard title={t.discountStatus}>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%" cy="50%"
                    innerRadius={60} outerRadius={95}
                    dataKey="value" nameKey="name"
                  >
                    {pieData.map((_, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center flex-wrap gap-4 mt-3">
                {pieData.map((entry, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-custom-letters">
                    <span className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                    <span>{entry.name}: <strong>{entry.value}</strong></span>
                  </div>
                ))}
              </div>
            </ChartCard>

            {/* Line: New discounts per month */}
            <ChartCard title={t.discountsPerMonth}>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={discountsPerMonth} margin={{ left: -10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0d7f7" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Line
                    type="monotone" dataKey="count" name={t.count}
                    stroke={isHighContrast ? "#b5a687" : "#D85FEC"} strokeWidth={2}
                    dot={{ r: 3, fill: isHighContrast ? "#b5a687" : "#D85FEC", stroke: isHighContrast ? "#b5a687" : "#D85FEC" }}
                    activeDot={{ r: 5, fill: isHighContrast ? "#c9bb9e" : "#D85FEC", stroke: isHighContrast ? "#000000" : "#fff" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Horizontal Bar: Discounts by category (full width) */}
            <ChartCard title={t.discountsByCategory} fullWidth>
              <ResponsiveContainer width="100%" height={Math.max(300, catData.length * 28)}>
                <BarChart data={catData} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0d7f7" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
                  <Tooltip cursor={false} />
                  <Bar dataKey="count" name={t.count} radius={[0, 4, 4, 0]}>
                    {catData.map((_, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Pie: Offers by city */}
            <ChartCard title={t.discountsByCity}>
              {cityHeatData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={cityHeatData.slice(0, 8)}
                        cx="50%" cy="50%"
                        outerRadius={95}
                        dataKey="size" nameKey="name"
                      >
                        {cityHeatData.slice(0, 8).map((_, idx) => (
                          <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        return (
                          <div className="bg-custom-header border border-gray-200 rounded-lg shadow px-3 py-2 text-xs text-custom-letters">
                            <p className="font-semibold mb-1">{d.name}</p>
                            <p>{t.count}: <strong>{d.size}</strong></p>
                          </div>
                        );
                      }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex justify-center flex-wrap gap-3 mt-3">
                    {cityHeatData.slice(0, 8).map((entry, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 text-xs text-custom-letters">
                        <span className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                        <span>{entry.name}: <strong>{entry.size}</strong></span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-custom-letters opacity-60 text-center py-10">—</p>
              )}
            </ChartCard>

            {/* Line: New users per month */}
            <ChartCard title={t.usersPerMonth}>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={usersPerMonth} margin={{ left: -10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0d7f7" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Line
                    type="monotone" dataKey="count" name={t.count}
                    stroke={isHighContrast ? "#b5a687" : "#817EFD"} strokeWidth={2}
                    dot={{ r: 3, fill: isHighContrast ? "#b5a687" : "#817EFD", stroke: isHighContrast ? "#b5a687" : "#817EFD" }}
                    activeDot={{ r: 5, fill: isHighContrast ? "#c9bb9e" : "#817EFD", stroke: isHighContrast ? "#000000" : "#fff" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Horizontal Bar: Top stores (full width) */}
            <ChartCard title={t.topStores} fullWidth action={
              <select
                value={topStoreCity}
                onChange={e => setTopStoreCity(e.target.value)}
                className="text-xs border rounded px-2 py-1 outline-none focus:ring-2 focus:ring-custom-grad1 bg-custom-background text-custom-letters"
              >
                <option value="">{t.allCities}</option>
                {topStoreCities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            }>
              {filteredStoreData.length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={filteredStoreData} margin={{ top: 10, right: 20, left: -10, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0d7f7" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" interval={0} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip cursor={false} content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div className="bg-custom-header border border-gray-200 rounded-lg shadow px-3 py-2 text-xs text-custom-letters">
                          <p className="font-semibold mb-1">{d.name}</p>
                          {d.city && <p className="opacity-70">{d.city}{d.area ? ` · ${d.area}` : ''}</p>}
                          <p className="mt-1">{t.count}: <strong>{d.count}</strong></p>
                        </div>
                      );
                    }} />
                    <Bar dataKey="count" name={t.count} radius={[4, 4, 0, 0]}>
                      {filteredStoreData.map((_, idx) => (
                        <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-custom-letters opacity-60 text-center py-10">—</p>
              )}
            </ChartCard>

          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
